// umlaut-check: skip — technische Schlüssel der Bestandskennzahlen (JSON-Schnittstelle).
import {qtyNum} from './format';
import {origOf} from './changes';
import {discountPlan, discountPreview} from './discount';
import {
    WH_OF_STAMM,
    availCat,
    freeOf,
    isExcluded,
    isSchneiderArticle,
    physOf,
    pickedOf,
    sourceWarehouseFor,
    stammLagerOf,
} from './availability';

// Fachlogik des geführten Ablaufs („Wizard", Original 3130–3331, 3552–3600,
// 4046–4105, 4651–4730). Reine Funktionen ohne React und ohne Netzzugriff:
// dieselbe Wahrheit für die Breadcrumb, die Knopf-Freigaben in den Schritten
// und den Rauchtest.
//
// Statt der globalen Variablen des Originals nimmt jede Funktion einen
// Kontext entgegen:
//   {mode, order, rows, stock, pending, kommWiz, progress, prodPlan, discManual}
// Die Reihenfolge der Prüfungen ist Fachlogik und wird exakt übernommen —
// jede Umstellung ändert im Betrieb, welcher Schritt angezeigt wird.

/** Lager-IDs, die der Ablauf unterscheidet (Original WH_OF_STAMM, SNV_ID). */
export const WH_LAHR = '3827';
export const SNV_ID = '8034';
export const COSMOS_ID = '2350209';

/**
 * Die neun Schritte (Original `WIZ_STEPS`, 3130–3140). `bars` nennt die
 * Leisten, die in diesem Schritt sichtbar sind; Titel und Beschriftung stehen
 * 1:1 wie im Original (ABGLEICH G1, G4, G10, G16, G21, G25, G30, G34, G36).
 */
export const WIZ_STEPS = [
    {
        n: 1,
        label: 'Bestand',
        bars: ['stockbar'],
        title: 'Bestand ansehen: je Position mit dem Schieberegler „Liefern ab" entscheiden, was ab Lahr und was '
            + 'ab Schneider läuft. Ob 1 oder 2 Lieferungen entstehen, ergibt sich daraus.',
    },
    {
        n: 2,
        label: 'Zahlung',
        bars: ['paybar', 'shipcostbar'],
        title: 'Auftragsart wählen, ggf. Kunden-Bestellnummer ergänzen und die Versandkosten schätzen, bevor die '
            + 'AB entsteht.',
    },
    {
        n: 3,
        label: 'Schneider-LS',
        bars: ['whbar'],
        title: 'Falls es einen Schneider-Anteil gibt: den Lieferschein ab Schneider (SNV) erzeugen.',
    },
    {
        n: 4,
        label: 'Komischein',
        bars: ['whbar', 'kommbar'],
        title: 'Kommissionierschein für Lahr erzeugen und ans Lager geben.',
    },
    {
        n: 5,
        label: 'Produktion',
        bars: ['prodbar'],
        title: 'Zu fertigende Artikel (Sets) prüfen und Produktionsaufträge erzeugen — fertigmelden in weclapp.',
    },
    {
        n: 6,
        label: 'Kommissionierung',
        bars: ['stockbar'],
        title: 'Ergebnis der Kommissionierung eintragen: was war da, was nicht, was wurde produziert — und die '
            + 'geprüft-Haken setzen.',
    },
    {
        n: 7,
        label: 'Rabatte',
        bars: ['discbar', 'chbar'],
        title: 'Rabatte anhand der echten, lieferbaren Mengen anwenden.',
    },
    {
        n: 8,
        label: 'Versand',
        bars: ['shipcostbar'],
        title: 'Versandkosten anhand der Berechnung hinzufügen.',
    },
    {
        n: 9,
        label: 'Lahr-LS',
        bars: ['whbar'],
        title: 'Lieferschein ab Lahr erzeugen und Auftrag abschließen.',
    },
];

export const WIZ_LAST = WIZ_STEPS.length;

/** Ausgangsstand der Wizard-Marker (Original `KOMM_WIZ`, jetzt serverseitig). */
export const emptyKommWiz = {schneiderDone: false, arrived: false, komm: false, verified: false};

/** Beschriftung des Sperrgrunds (Original `LOCK_LABEL`, 3277). */
const LOCK_LABEL = {
    CANCELLED: 'storniert',
    CLOSED: 'abgeschlossen',
    MANUALLY_CLOSED: 'manuell geschlossen (Rückstand verfallen)',
};

// ── Sperre ──────────────────────────────────────────────────────────────────

/** Storniert/abgeschlossen: der Wizard wird komplett abgeschaltet (G45). */
export function isLocked(ctx) {
    return !!(ctx && ctx.order && ctx.mode === 'order' && !ctx.order.editable);
}

export function lockReason(ctx) {
    const order = ctx && ctx.order;
    if (!order) {
        return '';
    }
    return LOCK_LABEL[order.status] || `im Status ${order.status}`;
}

// ── Lagerbestand und Split ──────────────────────────────────────────────────

/**
 * Deckung je Kandidatenlager (Original `warehouseAnalysis`, 4046): wie viele
 * Artikel sind dort voll gedeckt (all-or-nothing)? Empfehlung = wenigste
 * Beistellungen. Liefert `null`, solange der Bestand fehlt — daran hängt im
 * Original die gesamte Sichtbarkeit der Lieferleiste.
 */
export function warehouseAnalysis(rows, stock) {
    const warehouses = (stock && stock.warehouses) || [];
    const whStock = (stock && stock.whStock) || {};
    if (!warehouses.length || !Object.keys(whStock).length) {
        return null;
    }

    const need = {};
    for (const r of rows || []) {
        if (!r.del && r.articleId) {
            need[r.articleId] = (need[r.articleId] || 0) + qtyNum(r.quantity);
        }
    }

    const arts = Object.keys(need);
    const per = warehouses.map((w) => {
        let covered = 0;
        for (const aid of arts) {
            if (((whStock[aid] || {})[w.id] || 0) >= need[aid]) {
                covered++;
            }
        }
        return {id: w.id, name: w.name, covered, beistell: arts.length - covered};
    });
    const best = per.slice().sort((a, b) => a.beistell - b.beistell)[0];

    return {per, best, n: arts.length, need};
}

/**
 * Split-Plan (Original `splitPlan`, 4686, erweitert um `excludedRows` nach
 * PLAN.md 3.10 / ABGLEICH H29): Die Positions-Schieberegler entscheiden, ab
 * welchem Lager jede Position läuft.
 *
 * Kit-Kinder folgen ihrem eigenen Schieberegler — ein Set kann über zwei Läger
 * laufen. Der Kit-Kopf bleibt primär, solange mindestens ein zu lieferndes Kind
 * primär ist; sind alle Kinder ausgeschlossen, fällt auch der Kopf heraus.
 */
export function splitPlan(primaryWh, rows, stock) {
    const a = warehouseAnalysis(rows, stock);
    if (!a) {
        return null;
    }

    const wh = String(primaryWh);
    const secWh = ((stock && stock.warehouses) || []).find((w) => String(w.id) !== wh) || null;
    const kitParents = new Set((rows || []).filter((r) => r.parentItemId).map((r) => String(r.parentItemId)));
    const isKitHead = (r) => r.id != null && kitParents.has(String(r.id));
    const whOfRow = (r) => WH_OF_STAMM[stammLagerOf(r, stock)] || wh;

    // Cosmos ist ein eigenes, drittes Lager mit eigenem Lieferschein (R2) —
    // seine Positionen laufen weder ab Lahr noch ab Schneider. Ist Cosmos selbst
    // das Primärlager, gilt das nicht.
    const cosmosEigen = wh !== COSMOS_ID;
    const istCosmos = (r) => cosmosEigen && whOfRow(r) === COSMOS_ID;

    const primaryRows = [];
    const secondaryRows = [];
    const cosmosRows = [];
    const excludedRows = [];
    const heads = [];

    for (const r of rows || []) {
        if (r.del || !r.articleId) {
            continue;
        }
        if (isKitHead(r)) {
            heads.push(r);
            continue;
        }
        if (isExcluded(r)) {
            excludedRows.push(r);
            continue;
        }
        if (istCosmos(r)) {
            cosmosRows.push(r);
            continue;
        }
        (whOfRow(r) === wh ? primaryRows : secondaryRows).push(r);
    }

    for (const h of heads) {
        const kids = (rows || []).filter(
            (r) => !r.del && r.articleId && String(r.parentItemId) === String(h.id),
        );
        const geliefert = kids.filter((k) => !isExcluded(k));

        // Kopf ohne zu lieferndes Kind bzw. selbst ausgenommen: fällt aus beiden
        // Lieferscheinen heraus, sonst bliebe eine leere Kopfzeile stehen.
        if ((kids.length && !geliefert.length) || (!kids.length && isExcluded(h))) {
            excludedRows.push(h);
            continue;
        }

        const anyPrimary = !geliefert.length || geliefert.some((k) => whOfRow(k) === wh);
        const anySecondary = geliefert.some((k) => whOfRow(k) !== wh && !istCosmos(k));

        // Kopf folgt seinen Kindern: ab Lahr, sonst ab Schneider, sonst ab Cosmos.
        if (anyPrimary) {
            primaryRows.push(h);
        } else if (anySecondary || !geliefert.some(istCosmos)) {
            secondaryRows.push(h);
        } else {
            cosmosRows.push(h);
        }
    }

    return {
        primaryWh: wh,
        secWh,
        primaryRows,
        secondaryRows,
        cosmosRows,
        excludedRows,
        secondaryItemIds: secondaryRows.map((r) => String(r.id)),
        cosmosItemIds: cosmosRows.map((r) => String(r.id)),
        excludedItemIds: excludedRows.map((r) => String(r.id)),
    };
}

/**
 * Lahr-Positionen ohne physische Deckung und ohne Entnahme (Original
 * `uncoveredPrimaryRows`, 4715). Grundlage der Warnung in Schritt 3 und 9.
 */
export function uncoveredPrimaryRows(plan, rows, stock) {
    const a = warehouseAnalysis(rows, stock);
    if (!a || !plan) {
        return [];
    }
    return plan.primaryRows.filter((r) => {
        if (r.locked || !r.articleId) {
            return false;
        }
        // Verfügbar = Lagerbestand − von anderen Aufträgen reserviert; die eigene
        // Reservierung liegt schon im Lagerbestand und zählt nicht doppelt.
        const byWh = ((stock.freeByWh || {})[r.articleId] || {});
        const verfuegbar = byWh[plan.primaryWh] !== undefined
            ? byWh[plan.primaryWh]
            : (((stock.whStock || {})[r.articleId] || {})[plan.primaryWh] || 0);
        return verfuegbar < (a.need[r.articleId] || 0);
    });
}

/**
 * Farbdosen (Burner/Premium mit VOC-Wert) — Original `schneiderRows`, 4655.
 * Sie liegen im Palettenlager Schneider; die Prüfung selbst steckt in
 * `availability.isSchneiderArticle`.
 */
export function schneiderRows(rows, stock) {
    return (rows || []).filter(
        (r) => !r.del && r.articleId && !r._new && isSchneiderArticle(r, stock),
    );
}

/** Läuft ein Teil des Auftrags über Schneider (Original `hasSchneiderLS`, 3220)? */
export function hasSchneiderLS(ctx) {
    if (!ctx.order || ctx.mode !== 'order' || !ctx.stock.loaded) {
        return false;
    }
    const sp = splitPlan(WH_LAHR, ctx.rows, ctx.stock);
    return !!(sp && sp.secondaryRows.length > 0);
}

/**
 * „Wohin geht die Schneider-Ware?" (Original `renderWarehouse`, 4130–4135).
 * Der Wert ändert an den Belegen nichts — es entstehen ohnehin zwei
 * Lieferscheine — und landet nur im Liefer-Kommentar. Ohne echte Wahl (nur
 * ein Lager hat Ware) wird auf den passenden Einzel-Modus normiert; ein
 * hängengebliebener Beistell-Wert wird korrigiert.
 */
export function sourceNormalization(orderSource, plan) {
    const lahrN = plan ? plan.primaryRows.length : 0;
    const snvN = plan ? plan.secondaryRows.length : 0;
    const bothSides = lahrN > 0 && snvN > 0;
    const snvSmaller = snvN <= lahrN;
    const beistellSrc = snvSmaller ? 'lahr' : 'snv';

    let value = orderSource;
    if (!bothSides) {
        // Nur eine Seite hat Positionen: eine Sendung ab diesem Lager. Reine
        // Lahr-Aufträge liefen früher als 'split' und bekamen im
        // Liefer-Kommentar „ZWEI Sendungen" (AB5045, Ticket #121).
        value = snvN > 0 ? 'snv' : (lahrN > 0 ? 'lahr' : 'split');
    } else if (orderSource !== 'split' && orderSource !== beistellSrc) {
        value = beistellSrc;
    }

    return {lahrN, snvN, bothSides, snvSmaller, beistellSrc, value};
}

/** Der Lieferschein am SNV-Lager (Original `snvShipment`, 3170). */
export function snvShipment(order) {
    return ((order && order.shipments) || []).find((s) => String(s.warehouseId) === SNV_ID) || null;
}

/**
 * Vorschlag „Paket oder Palette" für den Schneider-Lieferschein (Original
 * `snvVersandInfo`, 3163): ~12 Dosen je Paket, ab ~120 Stück lohnt die Palette.
 */
export const SNV_STK_JE_PAKET = 12;
export const SNV_PALETTE_AB_STK = 120;

export function snvVersandInfo(rows) {
    const stk = (rows || []).reduce(
        (n, r) => n + Math.max(0, qtyNum(r.quantity) - qtyNum(r.shippedQuantity)),
        0,
    );
    const pakete = Math.ceil(stk / SNV_STK_JE_PAKET);
    return {stk, pakete, kind: stk >= SNV_PALETTE_AB_STK ? 'palette' : 'paket'};
}

/**
 * Das PDF zum Lieferschein (Original `docForShipment`, 3214). Versandetiketten
 * werden aussortiert — gemeint ist der Lieferschein selbst.
 */
export function docForShipment(ship, shipDocs) {
    if (!ship || !ship.id || !shipDocs || shipDocs.id !== String(ship.id) || !shipDocs.docs) {
        return null;
    }
    return shipDocs.docs
        .filter((d) => !/etikett/i.test(d.name || ''))
        .find((d) => /^LS-/i.test(d.name || '') || (d.name || '').includes(String(ship.number))) || null;
}

// ── „Erledigt"-Wahrheiten ───────────────────────────────────────────────────
// Sie werden bewusst von beiden Seiten benutzt: von der Breadcrumb und von den
// Knopf-Freigaben der Schritte (ABGLEICH G43). Sonst sperrte der Wizard gegen
// einen Beleg, der längst am Auftrag hängt.

export function schneiderLsDone(ctx) {
    return !!(snvShipment(ctx.order) || (ctx.kommWiz && ctx.kommWiz.schneiderDone));
}

export function kommScheinDone(ctx) {
    return !!((ctx.kommWiz && ctx.kommWiz.komm) || (ctx.progress && ctx.progress.kommDoc));
}

/** Gibt es im Auftrag Produktionsartikel (Original `hasProductionArticles`, 2295)? */
export function hasProductionArticles(ctx) {
    const production = (ctx.stock && ctx.stock.production) || {};
    return (ctx.rows || []).some((r) => !r.del && r.articleId && !r._new && production[String(r.articleId)]);
}

/**
 * Hat der Auftrag schon nachgelagerten Fortschritt (Original
 * `orderHasProgress`, 3147)? Im Original ausgewertet wurde das zuletzt nicht
 * mehr; übernommen, weil der Kasten „Auftrag löschen/stornieren" dieselbe Frage
 * stellt und die Beschriftung daran hängt.
 */
export function orderHasProgress(ctx) {
    return !!(ctx.progress && ctx.progress.kommDoc)
        || !!(ctx.prodPlan && (ctx.prodPlan.existingOrders || []).length)
        || !!(ctx.order && (ctx.order.shipments || []).length)
        || !!(ctx.order && ctx.order.status && ctx.order.status !== 'ORDER_ENTRY_IN_PROGRESS');
}

/**
 * Positionen, die laut weclapp gar nicht verfügbar sind (Original
 * `stockShortItems`, 3552). Positionen auf „Keine" haben keine Ampel und
 * halten den Schritt darum nicht offen (ABGLEICH H35).
 */
export function stockShortItems(rows, stock, order) {
    const out = [];
    for (const r of rows || []) {
        if (r.del || r.locked || r._new || !r.articleId) {
            continue;
        }
        if (availCat(r, stock, order) !== 0) {
            continue;
        }
        const need = qtyNum(r.quantity) - qtyNum(r.shippedQuantity);
        const free = Math.max((stock.loaded && freeOf(r, stock, order)) || 0, 0);
        const missing = Math.max(1, Math.ceil(need - free));
        out.push({
            articleId: r.articleId,
            articleNumber: r.articleNumber,
            title: r.title,
            quantity: missing,
        });
    }
    return out;
}

/**
 * Reservierbare Menge einer Position: Lagerbestand im Lieferlager − von ANDEREN
 * Aufträgen/Lieferscheinen hart reserviert (= verfügbar). Eine schon für diesen
 * Auftrag gesicherte Menge liegt darin noch drin. Ohne Bestandsdaten gilt der
 * Lagerbestand.
 */
export function reservableStock(r, stock, order) {
    const verfuegbar = stock.loaded ? freeOf(r, stock, order) : undefined;
    if (verfuegbar !== undefined) {
        return Math.max(0, verfuegbar);
    }
    return Math.max(0, physOf(r, stock, order) || 0);
}

/**
 * Sammel-Reservierung (Original `batchPickCandidates`, 3573): alle
 * Lahr-Positionen mit physisch noch reservierbarem Rest — auch die schon
 * „verfügbaren". „Verfügbar" heißt in weclapp nur „noch keiner hat sie
 * genommen".
 */
export function batchPickCandidates(rows, stock, order) {
    const out = [];
    const kitParents = new Set((rows || []).filter((r) => r.parentItemId).map((r) => String(r.parentItemId)));

    for (const r of rows || []) {
        if (r.del || r._new || !r.articleId || (r.id != null && kitParents.has(String(r.id)))) {
            continue;
        }
        if (sourceWarehouseFor(r, stock, order) !== WH_LAHR) {
            continue;
        }
        if (availCat(r, stock, order) === 3) {
            continue;
        }
        const need = qtyNum(r.quantity) - qtyNum(r.shippedQuantity);
        const picked = pickedOf(r);
        const reservable = Math.min(need, reservableStock(r, stock, order)) - picked;
        if (reservable > 0) {
            out.push({r, qty: Math.ceil(need - picked)});
        }
    }
    return out;
}

/**
 * Buchungsmenge je Richtung (Original `bookQtyFor`, 3915):
 * `pick` = Restmenge bis Bedarf, `in` = bis „verfügbar" den Bedarf deckt,
 * `out` = Senkung auf die von anderen Aufträgen reservierte Menge (Untergrenze
 * sind die fremden Reservierungen, sie bleiben gedeckt).
 */
export function bookQtyFor(r, direction, stock, order) {
    const freeRaw = (stock.loaded ? freeOf(r, stock, order) : 0) || 0;
    const need = qtyNum(r.quantity) - qtyNum(r.shippedQuantity);

    if (direction === 'pick') {
        return Math.max(1, Math.ceil(need - pickedOf(r)));
    }
    if (direction === 'in') {
        return Math.max(1, Math.ceil(need - freeRaw));
    }
    // Verfügbar = Lagerbestand − fremd reserviert: genau so viel darf ausgebucht werden.
    return Math.floor(Math.max(0, freeRaw));
}

/** Taugt die Position für eine Massenaktion (Original `massCand`, 3679)? */
export function massCand(r, dir, stock, order) {
    const need = qtyNum(r.quantity) - qtyNum(r.shippedQuantity);
    if (dir === 'pick') {
        return (Math.min(need, reservableStock(r, stock, order)) - pickedOf(r)) > 0;
    }
    if (dir === 'unpick') {
        return pickedOf(r) > 0;
    }
    if (dir === 'in') {
        return pickedOf(r) < need && availCat(r, stock, order) !== 2 && bookQtyFor(r, 'in', stock, order) > 0;
    }
    if (dir === 'out') {
        return bookQtyFor(r, 'out', stock, order) > 0;
    }
    return false;
}

/**
 * Bestandskorrektur beim Abhaken einer reduzierten Menge (Original
 * `stockCorrectionFor`, 3785): Im Lager lagen weniger Stück als bestellt, der
 * Bearbeiter hat die Menge gesenkt — dann stimmt der weclapp-Bestand nicht mehr.
 */
export function stockCorrectionFor(r, stock, order) {
    if (!r || r.del || r.locked || r._new || !r.articleId) {
        return null;
    }
    if (stammLagerOf(r, stock) !== 'Lahr') {
        return null;
    }
    const o = origOf(r);
    if (!o) {
        return null;
    }
    const orig = qtyNum(o.quantity);
    const found = qtyNum(r.quantity);
    if (!(found < orig)) {
        return null;
    }
    const phys = ((stock.whStock || {})[String(r.articleId)] || {})[sourceWarehouseFor(r, stock, order)];
    if (phys === undefined) {
        return null;
    }
    const out = Math.floor(phys - found);
    if (!(out > 0)) {
        return null;
    }
    return {orig, found, phys, out};
}

/** Schlüssel eines Prüf-Hakens (Original `stockCheckKey`, 3593). */
export function checkKeyOf(r) {
    return String(r.id || ('a' + r.articleId));
}

// ── Positions-Statusmatrix ──────────────────────────────────────────────────

/**
 * „📋 Positions-Status" (Original `deliveryStatusMatrix`, 4073): je Position
 * die Seite (Lahr/Schneider) und der Lieferstatus. Positionen auf „Keine"
 * stehen als „nicht geliefert (Keine)" und zählen in keinem Lieferschein
 * (PLAN.md 3.10).
 */
export function deliveryStatusMatrix(rows, stock, order) {
    const kitParents = new Set((rows || []).filter((r) => r.parentItemId).map((r) => String(r.parentItemId)));
    const zeilen = (rows || []).filter(
        (r) => !r.del && r.articleId && !r._new && !(r.id != null && kitParents.has(String(r.id))),
    );

    const lines = zeilen.map((r) => {
        const ordered = qtyNum(r.quantity);
        const shipped = qtyNum(r.shippedQuantity);
        const wh = sourceWarehouseFor(r, stock, order);
        const isLahr = wh === WH_LAHR;

        let side = {label: '—', cls: ''};
        if (wh === null) {
            side = {label: 'Keine', cls: ''};
        } else if (isLahr) {
            side = {label: 'Lahr', cls: 'lahr'};
        } else if (wh === SNV_ID) {
            side = {label: 'Schneider', cls: 'snv'};
        } else {
            const w = ((stock.warehouses || []).find((x) => String(x.id) === String(wh)) || {}).name;
            side = {label: w || '—', cls: ''};
        }

        let status;
        if (wh === null) {
            status = {label: 'nicht geliefert (Keine)', cls: ''};
        } else if (ordered > 0 && shipped >= ordered) {
            status = {label: '✓ geliefert', cls: 'ok'};
        } else if (shipped > 0) {
            status = {label: `teilgeliefert ${shipped}/${ordered}`, cls: 'part'};
        } else if (wh === COSMOS_ID) {
            status = {label: '→ ab Cosmos', cls: 'part'};
        } else if (!isLahr) {
            status = {label: '→ ab Schneider (SNV)', cls: 'snv'};
        } else {
            const rest = ordered - shipped;
            const c = availCat(r, stock, order);
            if (rest > 0 && pickedOf(r) >= rest) {
                status = {label: '🔒 gesichert', cls: 'ok'};
            } else if (c === 2) {
                status = {label: 'passt (gedeckt)', cls: 'ok'};
            } else if (c === 3) {
                status = {label: '⚙ Produktion', cls: 'part'};
            } else if (c === 1) {
                status = {label: 'teilweise da', cls: 'part'};
            } else if (c === 0) {
                status = {label: 'nicht da', cls: 'no'};
            } else {
                status = {label: '—', cls: ''};
            }
        }

        return {
            key: String(r.id || r._key || r.articleId),
            articleNumber: r.articleNumber || '',
            title: (r.title || '').slice(0, 34),
            side,
            status,
        };
    });

    return {shipments: (order && order.shipments) || [], lines};
}

// ── Fingerprint des Kommissionierscheins ────────────────────────────────────

/** FNV-1a, 32 Bit (Original `hash32`, 4666) — gleiche Rechnung wie im Server. */
export function hash32(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * Fingerabdruck über den Schein-Inhalt (Original `kommFingerprint`, 4674):
 * je Position `id:articleId:Menge:Seite` (L/S), sortiert. Er landet im
 * Dateinamen und verrät später, ob der Auftrag seit dem Erzeugen geändert wurde.
 */
export function kommFingerprint(plan) {
    if (!plan) {
        return '';
    }
    const part = (rows, side) => (rows || [])
        .filter((r) => r && !r.del && r.articleId)
        .map((r) => `${r.id}:${r.articleId}:${qtyNum(r.quantity)}:${side}`);

    return hash32([...part(plan.primaryRows, 'L'), ...part(plan.secondaryRows, 'S')].sort().join('|'));
}

/** Kommissionierschein unter den Dokumenten am Beleg (Original 2210). */
export function findKommDoc(docs) {
    return (docs || []).find((x) => /kommissionierschein_wizard/i.test(x.name || ''))
        || (docs || []).find((x) => /kommission/i.test(x.name || ''))
        || null;
}

/**
 * Beide Wizard-Scheine: zusammengefasst zuerst, einzeln danach (Ticket #126,
 * F2 = a; im Original 2216 umgekehrt). Fällt auf den einzelnen (oder den alten) Schein zurück, solange nur
 * einer am Beleg hängt.
 */
export function findKommDocs(docs) {
    const wiz = (docs || []).filter((x) => /kommissionierschein_wizard/i.test(x.name || ''));
    if (wiz.length) {
        return wiz.slice().sort(
            (a, b) => (/zusammengefasst/i.test(a.name || '') ? 0 : 1) - (/zusammengefasst/i.test(b.name || '') ? 0 : 1),
        );
    }
    const k = findKommDoc(docs);
    return k ? [k] : [];
}

/** Eingebetteten Fingerabdruck aus einem Dateinamen lesen (Original 4679). */
export function kommFpOf(name) {
    const m = String(name || '').match(/_fp([0-9a-f]{8})\.pdf$/i);
    return m ? m[1].toLowerCase() : null;
}

// ── Schritt-Regeln ──────────────────────────────────────────────────────────

/**
 * Schritt ohne Aufgabe (Original `wizSkip`, 3230). Solange der Bestand nicht
 * geladen ist, wird NIE etwas übersprungen (ABGLEICH G41).
 */
export function wizSkip(s, ctx) {
    if (!ctx.order || ctx.mode !== 'order' || !ctx.stock.loaded) {
        return false;
    }
    if (s.n === 3) {
        return !hasSchneiderLS(ctx);
    }
    if (s.n === 5) {
        if (!hasProductionArticles(ctx)) {
            return true;
        }
        if (!ctx.prodPlan) {
            return false;
        }
        const nichtsHerzustellen = (ctx.prodPlan.toProduce || []).every((p) => p.need <= 0);
        return nichtsHerzustellen && (ctx.prodPlan.existingOrders || []).length === 0;
    }
    if (s.n === 7) {
        return !!ctx.stock.groups
            && !ctx.discManual
            && discountPreview(ctx.rows, ctx.stock, ctx.order, (ctx.pending || {}).channel).length === 0;
    }
    return false;
}

/** Häkchen-Wahrheit je Schritt (Original `wizDone`, 3244). */
export function wizDone(s, ctx) {
    if (!ctx.order) {
        return false;
    }
    const p = ctx.order.payment;
    const pending = ctx.pending || {};

    switch (s.n) {
        case 1:
            return ctx.stock.loaded;
        case 2:
            return !!(p && (!p.suggested || p.suggested === p.current));
        case 3:
            return !hasSchneiderLS(ctx) || schneiderLsDone(ctx);
        case 4:
            return kommScheinDone(ctx);
        case 5:
            return !hasProductionArticles(ctx)
                || !!(ctx.prodPlan && (ctx.prodPlan.existingOrders || []).length > 0);
        case 6:
            return ctx.stock.loaded && stockShortItems(ctx.rows, ctx.stock, ctx.order).length === 0;
        case 7:
            return !!(ctx.mode === 'order' && ctx.stock.groups
                && discountPlan(ctx.rows, ctx.stock, ctx.order).every((pl) => pl.pct === null || pl.toChange === 0));
        case 8:
            // Gibt es eine Schätzung aus Schritt 2, gilt der Schritt erst nach
            // dem Abgleich mit dem echten Stand als erledigt (Ticket #119).
            if (ctx.shipEstimate && !ctx.shipEstimate.confirmedAt) {
                return false;
            }
            return (ctx.order.shippingCosts || []).length > 0 || (pending.shipCost || []).length > 0;
        case 9:
            return (ctx.order.shipments || []).length > 0;
        default:
            return false;
    }
}

/**
 * Nächster bzw. voriger nicht übersprungener Schritt (Original `wizAdjacent`,
 * 3263). `null` deaktiviert den Navigationsknopf.
 */
export function wizAdjacent(from, dir, ctx) {
    for (let n = from + dir; n >= 1 && n <= WIZ_LAST; n += dir) {
        if (!wizSkip(WIZ_STEPS[n - 1], ctx)) {
            return n;
        }
    }
    return null;
}

/** Schritt-Definition zu einer Nummer. */
export function stepOf(n) {
    return WIZ_STEPS.find((s) => s.n === n) || WIZ_STEPS[0];
}

/** Ist die Leiste in diesem Schritt sichtbar (Original `renderWizard`, 3289)? */
export function barVisible(bar, stepNumber) {
    return stepOf(stepNumber).bars.includes(bar);
}

/**
 * Tabelle sichtbar? Original: Schritt 1, Schritt 6 und Schritt 7 nur mit
 * aufgeklappter Handeingabe; im Lieferschein-Modus immer (ABGLEICH E93).
 */
export function tableVisible(stepNumber, mode, discManual) {
    if (mode !== 'order') {
        return true;
    }
    return stepNumber === 1 || stepNumber === 6 || (stepNumber === 7 && !!discManual);
}
